// ─────────────────────────────────────────────────────────────────────────────
// Module Access Matrix — single source of truth for menus + route guards.
//
// Levels: "full" | "view" | "own" | "none"
// "full" = create/edit/delete   "view" = read-only
// "own"  = restricted to own records
// "none" = no menu, no route
// ─────────────────────────────────────────────────────────────────────────────

export const ROLES = {
  ADMIN:             "admin",            // Super Admin (MediMagic internal)
  STATE_ADMIN:       "state_admin",
  UNIVERSITY_ADMIN:  "university_admin",
  COLLEGE_ADMIN:     "college_admin",
  IT_ADMIN:          "it_admin",
  SUPPORT_USER:      "support_user",
  FACULTY:           "faculty",
};

export const ROLE_LABEL = {
  admin:            "Super Admin",
  state_admin:      "State Admin",
  university_admin: "University Admin",
  college_admin:    "College Admin",
  it_admin:         "IT Admin",
  support_user:     "Support User",
  faculty:          "Faculty",
};

// Modules: dashboard, courses, master_data, timetable, users, tickets, contact, notifications
export const ACCESS = {
  admin: {
    dashboard:     "full",
    courses:       "full",
    master_data:   "full",
    timetable:     "full",
    users:         "full",
    tickets:       "full",
    contact:       "full",
    notifications: "full",
  },
  state_admin: {
    dashboard:     "view",
    courses:       "none",
    master_data:   "none",
    timetable:     "none",
    users:         "none",
    tickets:       "full",   // view + create + reply (no triage)
    contact:       "view",
    notifications: "full",
  },
  university_admin: {
    dashboard:     "view",
    courses:       "none",
    master_data:   "none",
    timetable:     "none",
    users:         "none",
    tickets:       "full",
    contact:       "view",
    notifications: "full",
  },
  college_admin: {
    dashboard:     "view",
    courses:       "none",
    master_data:   "none",
    timetable:     "view",
    users:         "view",
    tickets:       "full",
    contact:       "view",
    notifications: "full",
  },
  it_admin: {
    dashboard:     "view",
    courses:       "none",
    master_data:   "none",
    timetable:     "full",
    users:         "full",  // scoped to own college
    tickets:       "full",
    contact:       "view",
    notifications: "full",
  },
  support_user: {
    dashboard:     "view",
    courses:       "none",
    master_data:   "none",
    timetable:     "full",
    users:         "none",
    tickets:       "full",
    contact:       "view",
    notifications: "view",
  },
  faculty: {
    dashboard:     "view",
    courses:       "none",
    master_data:   "none",
    timetable:     "none",  // faculty uses Calendar, not Timetable
    calendar:      "view",  // own subject only (handled by backend scoping)
    users:         "none",
    tickets:       "full",
    contact:       "view",
    notifications: "view",
  },
};

export function can(role, moduleKey) {
  return ACCESS[role]?.[moduleKey] || "none";
}

export function canSee(role, moduleKey) {
  return can(role, moduleKey) !== "none";
}

export function isSuperAdmin(role) {
  return role === ROLES.ADMIN;
}

// All non-admin, non-superadmin roles use the shared user-portal layout.
export const PORTAL_ROLES = [
  "state_admin", "university_admin", "college_admin",
  "it_admin", "support_user", "faculty",
];
